import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface ExplorerConfig {
  general: {
    explorer_name?: string;
    tagline?: string;
    contact_email?: string;
    support_email?: string;
    description?: string;
    default_language?: string;
    timezone?: string;
    powered_by_text?: string;
    powered_by_link?: string;
    footer_description?: string;
    donation_address?: string;
    show_donation?: boolean;
    add_network_button_text?: string;
    add_network_logo_url?: string;
    show_add_network_button?: boolean;
  };
  branding: {
    logo_light_url?: string;
    logo_dark_url?: string;
    favicon_url?: string;
    native_coin_logo_url?: string;
    footer_logo_url?: string;
    og_image_url?: string;
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
    header_bg_color?: string;
    address_text_color?: string;
    txn_text_color?: string;
    block_text_color?: string;
  };
  network: {
    network_name?: string;
    chain_id?: string;
    native_token_symbol?: string;
    native_token_decimals?: string;
    rpc_url?: string;
    ws_url?: string;
    network_type?: string;
    block_time?: string;
    token_standard?: string;
  };
  seo: {
    meta_title?: string;
    meta_description?: string;
    meta_keywords?: string;
    google_analytics_id?: string;
    google_verification?: string;
  };
  about_page_images: {
    hero_banner?: string;
    gallery_left_top?: string;
    gallery_left_bottom?: string;
    gallery_middle?: string;
    gallery_right?: string;
    careers_image?: string;
    support_map?: string;
  };
  roadmap: Array<{
    year: string;
    events: Array<{ month: string; desc: string }>;
  }>;
  social_links: Record<string, string>;
  about_banners: Array<Record<string, string>>;
  careers: Array<Record<string, string>>;
  brand_assets: Array<Record<string, string>>;
  features: Record<string, boolean>;
  terms_content?: {
    last_updated?: string;
    content?: string;
  };
  bug_bounty_content?: {
    content?: string;
    hall_of_fame?: Array<{ name: string; link: string }>;
  };
  ad_preview_images?: {
    home_page_banner?: string;
    search_ad?: string;
    banner_ad?: string;
    header_text_ad?: string;
    button_ad?: string;
    newsletter_sponsorship?: string;
  };
  priority_support_images?: {
    token_update?: string;
    smart_contract?: string;
    other_issues?: string;
  };
}

interface ExplorerConfigContextType {
  config: ExplorerConfig | null;
  isLoading: boolean;
  refetch: () => void;
}

const defaultContext: ExplorerConfigContextType = {
  config: null,
  isLoading: true,
  refetch: () => {},
};

const ExplorerConfigContext = createContext<ExplorerConfigContextType>(defaultContext);

export function ExplorerConfigProvider({ children }: { children: React.ReactNode }) {
  const [ config, setConfig ] = useState<ExplorerConfig | null>(null);
  const [ isLoading, setIsLoading ] = useState(true);

  const fetchSettings = useCallback(async() => {
    try {
      const res = await fetch('/api/public/explorer-settings');
      if (res.ok) {
        const data = await res.json();
        console.log('Fetched explorer config:', data);
        console.log('About page images:', data.about_page_images);
        console.log('Ad preview images:', data.ad_preview_images);
        setConfig(data);
      } else {
        console.error('Failed to fetch settings, status:', res.status);
      }
    } catch (err) {
      console.error('Failed to fetch explorer settings:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [ fetchSettings ]);

  return (
    <ExplorerConfigContext.Provider value={{ config, isLoading, refetch: fetchSettings }}>
      { children }
    </ExplorerConfigContext.Provider>
  );
}

export function useExplorerConfig() {
  return useContext(ExplorerConfigContext);
}

export default ExplorerConfigContext;

